import styled from '@emotion/styled';
import type { FunnelOffer, StepPageContent, StepPageBlock, DisplaySettings } from '../types';
import type { BlockCallbacks } from './ComponentRenderers';
import { normalizeBlocks, renderBlocks, blocksHaveCta } from './BlockRenderer';

interface FunnelPageContentProps {
  offer: FunnelOffer;
  offers?: FunnelOffer[];
  selectedOfferId?: string;
  onSelectOffer?: (offerId: string) => void;
  pageContent?: StepPageContent | StepPageBlock[] | null;
  errorMessage?: string | null;
  accepting: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onDismiss: () => void;
  /** When true, renders as a full-page experience (no max-width, no border). Used by PostPurchasePage. */
  fullPage?: boolean;
  displaySettings?: DisplaySettings | null;
}

const Wrapper = styled.section<{ $fullPage?: boolean }>`
  width: ${({ $fullPage }) => ($fullPage ? '100vw' : '100%')};
  max-width: ${({ $fullPage }) => ($fullPage ? '100vw' : '920px')};
  margin: ${({ $fullPage }) => ($fullPage ? '0' : '16px auto')};
  background: ${({ theme }) => theme.colors.background.primary};
  border: ${({ $fullPage, theme }) => ($fullPage ? 'none' : `1px solid ${theme.colors.border.primary}`)};
  border-radius: ${({ $fullPage, theme }) => ($fullPage ? '0' : theme.radius.regular)};
  overflow: hidden;
  ${({ $fullPage }) => $fullPage && 'flex: 1; min-height: 100vh; display: flex; flex-direction: column; position: relative;'}
`;

const Header = styled.div<{ $fullPage?: boolean }>`
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px;
  ${({ $fullPage }) => $fullPage && 'position: absolute; top: 0; right: 0; z-index: 2;'}
`;

const DismissButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  font-size: 16px;
`;

const Content = styled.div<{ $fullPage?: boolean }>`
  padding: ${({ $fullPage }) => ($fullPage ? '0' : '20px')};
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  width: 100%;
`;

const BlockCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: ${({ theme }) => theme.radius.regular};
  padding: 14px;
`;

const OfferImage = styled.img`
  width: 100%;
  max-height: 240px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.regular};
`;

const Title = styled.h3`
  margin: 0 0 8px;
  font-size: 24px;
  line-height: 1.2;
`;

const Text = styled.p`
  margin: 0;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OldPrice = styled.span`
  text-decoration: line-through;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const NewPrice = styled.span`
  font-size: 24px;
  font-weight: 700;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AcceptButton = styled.button`
  width: 100%;
  border: none;
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.regular};
  font-weight: 700;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.primary};
`;

const DeclineButton = styled.button`
  border: none;
  background: transparent;
  text-decoration: underline;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const OfferSelector = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const OfferSelectorButton = styled.button<{ $selected: boolean }>`
  border: 1px solid ${({ theme, $selected }) =>
    $selected ? theme.colors.text.primary : theme.colors.border.primary};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.text.primary : theme.colors.background.primary};
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.background.primary : theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.regular};
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

function formatPrice(value: number | null | undefined): string {
  if (!Number.isFinite(value)) return '$0.00';
  return `$${(value as number).toFixed(2)}`;
}

export function FunnelPageContent({
  offer,
  offers,
  selectedOfferId,
  onSelectOffer,
  pageContent,
  errorMessage,
  accepting,
  onAccept,
  onDecline,
  onDismiss,
  fullPage,
  displaySettings: _displaySettings,
}: FunnelPageContentProps) {
  // displaySettings available for future page layout customization (alignment, max-width, etc.)
  void _displaySettings;
  const blocks = normalizeBlocks(pageContent);
  const hasCtaBlock = blocksHaveCta(blocks);
  const callbacks: BlockCallbacks = { onAccept, onDecline, accepting };
  const multiOffers = Array.isArray(offers) && offers.length > 1 ? offers : null;

  return (
    <Wrapper role={fullPage ? 'region' : 'dialog'} aria-label="Funnel page experience" $fullPage={fullPage}>
      <Header $fullPage={fullPage}>
        <DismissButton type="button" aria-label="Dismiss offer" onClick={onDismiss}>
          ×
        </DismissButton>
      </Header>
      <Content $fullPage={fullPage}>
        {multiOffers && onSelectOffer && (
          <OfferSelector aria-label="Offer selector">
            {multiOffers.map((item, index) => {
              const isSelected = (selectedOfferId ?? offer.id) === item.id || (!selectedOfferId && index === 0);
              return (
                <OfferSelectorButton
                  key={item.id}
                  type="button"
                  $selected={isSelected}
                  aria-pressed={isSelected}
                  onClick={() => onSelectOffer(item.id)}
                >
                  {item.name || item.headline || `Offer ${index + 1}`}
                </OfferSelectorButton>
              );
            })}
          </OfferSelector>
        )}
        {blocks.length > 0 ? (
          renderBlocks(blocks, offer, callbacks, offers)
        ) : (
          <BlockCard>
            {offer.imageUrl && <OfferImage src={offer.imageUrl} alt={offer.headline ?? 'Offer'} />}
            <Title>{offer.headline}</Title>
            <Text>{offer.description}</Text>
            <PriceRow>
              <OldPrice>{formatPrice(offer.originalPrice)}</OldPrice>
              <NewPrice>{formatPrice(offer.pricingValue)}</NewPrice>
            </PriceRow>
          </BlockCard>
        )}
        {errorMessage && (
          <Text style={{ color: '#b42318', marginTop: 12 }}>
            {errorMessage}
          </Text>
        )}
        {!hasCtaBlock && (
          <Actions>
            <AcceptButton type="button" onClick={onAccept} disabled={accepting}>
              {accepting ? 'Adding...' : offer.ctaText}
            </AcceptButton>
            <DeclineButton type="button" onClick={onDecline} disabled={accepting}>
              {offer.declineText}
            </DeclineButton>
          </Actions>
        )}
      </Content>
    </Wrapper>
  );
}
